import React, { useState, useRef } from 'react';
import {
  XIcon,
  FolderUpIcon,
  AlertCircleIcon,
  CheckCircleIcon } from
'lucide-react';
interface ImportCSVModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (file: File) => void;
}
export function ImportCSVModal({
  isOpen,
  onClose,
  onImport
}: ImportCSVModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  if (!isOpen) return null;
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        setSelectedFile(file);
      }
    }
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };
  const handleImport = () => {
    if (selectedFile) {
      onImport(selectedFile);
      setSelectedFile(null);
      onClose();
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-sm"
        onClick={onClose} />
      
      <div className="relative w-full max-w-[600px] bg-surface-light rounded-[12px] shadow-xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between p-5 border-b border-border bg-muted/30">
          <div>
            <h2 className="font-heading text-lg font-semibold text-iron-grey">
              Import Employee Data
            </h2>
            <p className="font-body text-sm text-text-secondary mt-0.5">
              Upload a CSV file with employee information
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-iron-grey transition-colors">
            
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-5">
          <div className="bg-info-background border-l-4 border-primary rounded-r-[8px] p-4 flex items-start gap-3">
            <AlertCircleIcon className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div>
              <h3 className="font-body text-sm font-semibold text-iron-grey">
                CSV Format Requirements
              </h3>
              <p className="font-body text-sm text-text-secondary mt-1">
                Your CSV must include: First Name, Last Name, Email, Employment
                Type, Track, Start Date
              </p>
            </div>
          </div>

          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-[8px] p-8 text-center cursor-pointer transition-colors ${dragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary'}`}>
            
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden" />
            
            <FolderUpIcon className="w-12 h-12 text-text-tertiary mx-auto mb-3" />
            <p className="font-body text-sm text-iron-grey mb-1">
              {selectedFile ?
              selectedFile.name :
              'Click to upload or drag and drop'}
            </p>
            <p className="font-body text-xs text-text-secondary">
              CSV files only, max 5MB
            </p>
          </div>

          {selectedFile &&
          <div className="bg-success-background border-l-4 border-aquamarine rounded-r-[8px] p-4 flex items-start gap-3">
              <CheckCircleIcon className="w-5 h-5 text-aquamarine shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-body text-sm font-semibold text-iron-grey">
                  File Ready
                </h3>
                <p className="font-body text-sm text-text-secondary mt-1">
                  {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)}{' '}
                  KB)
                </p>
              </div>
            </div>
          }
        </div>

        <div className="p-5 border-t border-border bg-background-light flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 font-body text-sm font-medium text-iron-grey bg-surface-light border border-border rounded-[6px] hover:bg-muted transition-colors">
            
            Cancel
          </button>
          <button
            disabled={!selectedFile}
            onClick={handleImport}
            className="px-4 py-2 font-body text-sm font-medium text-white bg-primary rounded-[6px] hover:bg-primary-hover transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
            
            Import CSV
          </button>
        </div>
      </div>
    </div>);

}